import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, CheckSquare, FileText, ReceiptText } from "lucide-react";
import { useTranslation } from "react-i18next";
import FiscalSettings from "./FiscalSettings";
import { FiscalXmlImportsTab } from "@/components/erp/FiscalXmlImportsTab";
import { Badge } from "@/components/ui/badge";
import { Button, SegmentedTabs } from "@akuna/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@akuna/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TAXES_TABS, getTaxesTabFromPath } from "@/lib/taxes/navigation";

function TaxesPlaceholder({
  title,
  description,
  action,
  variant,
}: {
  title: string;
  description: string;
  action: string;
  variant: "history" | "invoices";
}) {
  const { t } = useTranslation("erp");

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <Button disabled>
            {variant === "history" ? <FileText className="mr-2 h-4 w-4" /> : <CheckSquare className="mr-2 h-4 w-4" />}
            {action}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor={`taxes-${variant}-start-date`}>
              {t("taxes.filters.startDate")}
            </label>
            <Input id={`taxes-${variant}-start-date`} type="date" disabled />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor={`taxes-${variant}-end-date`}>
              {t("taxes.filters.endDate")}
            </label>
            <Input id={`taxes-${variant}-end-date`} type="date" disabled />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t("taxes.filters.invoiceType")}</label>
            <Select disabled value="all">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("taxes.invoiceTypes.all")}</SelectItem>
                <SelectItem value="nfce">{t("taxes.invoiceTypes.nfce")}</SelectItem>
                <SelectItem value="nfse">{t("taxes.invoiceTypes.nfse")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-background text-muted-foreground">
            {variant === "history" ? <ReceiptText className="h-5 w-5" /> : <CalendarDays className="h-5 w-5" />}
          </div>
          <p className="text-sm font-medium text-foreground">{t(`taxes.${variant}.emptyTitle`)}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t(`taxes.${variant}.emptyDescription`)}</p>
          <div className="mt-4 flex justify-center gap-2">
            <Badge variant="outline">NFC-e</Badge>
            <Badge variant="outline">NFS-e</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Taxes() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("erp");
  const activeTab = getTaxesTabFromPath(location.pathname);

  if (location.pathname === "/taxes") {
    return <Navigate to="/taxes/settings" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-foreground">{t("taxes.title")}</h1>
        <p className="text-muted-foreground">{t("taxes.description")}</p>
      </div>

      <SegmentedTabs
        value={activeTab}
        onValueChange={(value) => {
          const tab = TAXES_TABS.find((item) => item.id === value);
          if (tab) navigate(tab.path);
        }}
        variant="outline"
        items={TAXES_TABS.map((tab) => ({
          value: tab.id,
          label: t(`taxes.${tab.labelKey}`),
        }))}
      />

      {activeTab === "settings" ? <FiscalSettings showHeader={false} backTo="/taxes/invoices" /> : null}

      {activeTab === "invoicesHistory" ? (
        <TaxesPlaceholder
          title={t("taxes.history.title")}
          description={t("taxes.history.description")}
          action={t("taxes.history.export")}
          variant="history"
        />
      ) : null}

      {activeTab === "invoices" ? (
        <TaxesPlaceholder
          title={t("taxes.invoices.title")}
          description={t("taxes.invoices.description")}
          action={t("taxes.invoices.issueSelected")}
          variant="invoices"
        />
      ) : null}

      {activeTab === "xmlImports" ? <FiscalXmlImportsTab /> : null}
    </div>
  );
}
